import env from 'env';
import { getAppTransport } from 'api/appTransport';
import { refreshTeamsSsoToken } from 'api/teamsSsoSession';

/** An HTTP failure retaining its body for shared API error formatting. */
export class SseHttpError extends Error {
  constructor(readonly response: { status: number; data: unknown }) {
    super(`Stream request failed: ${response.status}`);
    this.name = 'SseHttpError';
  }
}

/** Open a backend stream with the same authentication and redirect policy as Axios. */
export async function fetchSse(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const policy = getAppTransport();
  const refresh = refreshTeamsSsoToken();
  if (refresh) await refresh;
  init.signal?.throwIfAborted();
  const headers = new Headers(init.headers);
  for (const [key, value] of Object.entries(policy.getAuthHeaders())) {
    if (value) headers.set(key, value);
  }
  const response = await fetch(`${env.REACT_APP_SERVER_URL}${path}`, {
    ...init,
    headers,
  });
  if (response.ok) return response;

  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    init.signal?.throwIfAborted();
    // swallow-ok: Non-JSON proxy errors still carry a useful HTTP status.
    console.error('Unreadable stream HTTP error response:', error);
  }
  init.signal?.throwIfAborted();
  const failure = { status: response.status, data };
  policy.handleAuthFailure(failure, headers);
  throw new SseHttpError(failure);
}

/**
 * Server-Sent-Events reader shared by every streaming endpoint in the app:
 * the notes AI chat, the staffing chat, notes candidate sourcing, notes job
 * recommendations, agentic sourcing, and bulk-operation progress.
 *
 * Frames are separated by a blank line. Within a frame this reads the `event:`
 * and `data:` fields; `id:`, comment lines, and unknown fields are ignored.
 * Some backends put the discriminator on the `event:` line (bulk operations,
 * agentic sourcing) and others carry it inside the JSON payload (both chats),
 * so `SseMessage` exposes the event name and the parsed data side by side and
 * lets each caller pick.
 */

/** Terminator some endpoints send in place of a JSON payload. */
const DONE_SENTINEL = '[DONE]';

export interface SseMessage {
  /** The frame's `event:` field, or 'message' when it has none. */
  event: string;
  /**
   * The frame's `data:` payload, JSON-parsed but still untrusted. Consumers
   * must narrow this unknown value with event-specific guards before using it.
   */
  data: unknown;
}

export interface ReadSseStreamOptions {
  onMessage: (message: SseMessage) => void;
  /**
   * Called when a frame's data is not valid JSON. The frame is skipped either
   * way — one malformed frame must not tear down a stream that is still
   * delivering good ones. Defaults to logging.
   */
  onParseError?: (error: unknown, raw: string) => void;
  /**
   * Polled before each frame. A caller that cancels while handling a terminal
   * event (e.g. `sourcing_completed`) still sees that event dispatched — it is
   * what triggered the cancel — but no frame after it is delivered.
   */
  isCancelled?: () => boolean;
}

interface SseFrame {
  event: string;
  /** Still a string here; parsing is the reader's job so it can report failures. */
  data: string;
}

/**
 * Split one frame into its event name and raw data payload. Multiple `data:`
 * lines are joined with a newline, per the SSE spec. Returns null for frames
 * that carry no data at all (comments, lone `id:` lines, keep-alives).
 */
function parseSseFrame(frame: string): SseFrame | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    // Blank lines and `:`-prefixed comments carry no fields.
    if (!line || line.startsWith(':')) continue;

    const separator = line.indexOf(':');
    const field = separator === -1 ? line : line.slice(0, separator);
    let value = separator === -1 ? '' : line.slice(separator + 1);
    // The spec strips exactly one leading space after the colon, so a payload
    // whose own first character is a space survives intact.
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

const logParseError = (error: unknown, raw: string) => {
  console.error('Failed to parse streamed content: ', { error, raw });
};

/**
 * Read `response` to completion, dispatching one `onMessage` per SSE frame.
 *
 * Resolves when the stream ends. Rejects on transport failures (including the
 * AbortError raised when a caller aborts the underlying fetch) and exceptions
 * thrown by `onMessage` or `onParseError`. Callers that support cancellation
 * should filter AbortError out of their own error paths.
 */
export async function readSseStream(
  response: Response,
  { onMessage, onParseError = logParseError, isCancelled }: ReadSseStreamOptions
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  const dispatch = (rawFrame: string) => {
    const frame = parseSseFrame(rawFrame);
    if (!frame || frame.data === DONE_SENTINEL) return;

    let data: unknown;
    try {
      data = JSON.parse(frame.data);
    } catch (error) {
      onParseError(error, frame.data);
      return;
    }

    // Deliberately outside the try above: an exception thrown by a consumer is
    // a bug in that consumer and must surface, not be swallowed and mislabeled
    // as a malformed payload.
    onMessage({ event: frame.event, data });
  };

  try {
    while (!isCancelled?.()) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      // The trailing element is an incomplete frame; hold it for the next read.
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        if (isCancelled?.()) return;
        dispatch(frame);
      }
    }

    // Flush the decoder and process whatever is left. A well-formed stream ends
    // with a blank line and leaves nothing here, but if it closes on the last
    // event without that terminator the event would otherwise be stranded in
    // the buffer and silently dropped — e.g. a trailing `navigate` event, which
    // would render its tool chip (sent earlier) while the route push never fires.
    buffer += decoder.decode();
    if (!isCancelled?.() && buffer.trim()) dispatch(buffer);
  } finally {
    // Releases the connection when a consumer throws or the caller cancels
    // mid-stream; a no-op once the body has been drained normally.
    // swallow-ok: Reader cancellation only releases an ended/cancelled stream; transport or consumer errors from the read loop still reject the caller and are never replaced.
    void reader.cancel().catch(() => undefined);
  }
}
